import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as toDoActions from '../../features/todos/actions';
import * as authActions from '../../features/auth/actions';
import * as fromActions from '../../features/legal-entities/actions';
import * as fromAllUsersActions from '../../features/legal-entities/actions/all-users.actions';
import { fromGetBusinessDetailsActions, clearGetProofBusinessFicaDocs } from '../../features/businesses-detail/actions';
import { IBusinessUserRolesRequest } from '../../features/auth/models/business-user-roles.model';
import { ILegalEntitiesBusinessesResponse } from '../../shared/models/shared.model';
import { LegalEntityHistoryService } from '../../shared/legal-entity-history.service';
import { SharedService } from '../../shared/shared.service';
import { EMPTY_GUID } from '../../shared/constants';
import { SessionStorageIds } from '../../shared/enums';
import { PermissionService } from './permission.service';

/**
 * Makes a legal entity the current context, the same way picking one in the
 * legal entity search popup does (session, permissions, store, history).
 */
@Injectable({
  providedIn: 'root'
})
export class LegalEntitySelectionService {
  constructor(
    private store: Store,
    private sharedService: SharedService,
    private permissionService: PermissionService,
    private legalEntityHistoryService: LegalEntityHistoryService
  ) {}

  select(legalEntity: ILegalEntitiesBusinessesResponse): void {
    this.legalEntityHistoryService.addToHistory(legalEntity);

    sessionStorage.setItem('IsFromToDo', 'false');
    this.sharedService.isFromLegalEntities = true;

    // Set sessionStorage before store dispatches so side nav and other subscribers see the new entity when they react to the store
    sessionStorage.setItem('businessId', legalEntity.legalEntityId);
    sessionStorage.setItem('legalEntity', JSON.stringify(legalEntity));
    this.updatePermissionsForLegalEntity(legalEntity.legalEntityId);

    this.store.dispatch(toDoActions.loadRegulationVerified({ businessId: legalEntity.legalEntityId }));
    this.store.dispatch(fromActions.clearBusinessSelected());
    this.store.dispatch(fromActions.clearAssetManagerViewLinkedBusinesses());
    this.store.dispatch(fromActions.loadClearManageOfferings());
    this.store.dispatch(fromActions.saveSelectedBusinessId({ save: legalEntity.legalEntityId }));
    this.store.dispatch(toDoActions.loadSelectedLegalEntity({ selectedLE: legalEntity }));

    this.store.dispatch(fromGetBusinessDetailsActions.clearGetBusinessDetailsResponse());
    this.store.dispatch(clearGetProofBusinessFicaDocs());

    const userRolesReq: IBusinessUserRolesRequest = {
      businessId: legalEntity.legalEntityId,
      token: sessionStorage.getItem(SessionStorageIds.TOKEN) || '',
      uniqueDeviceId: EMPTY_GUID
    };
    this.store.dispatch(authActions.loadBusinessUserRoles({ request: userRolesReq }));

    this.store.dispatch(fromAllUsersActions.getBusiness({ businessId: legalEntity.legalEntityId }));
  }

  private updatePermissionsForLegalEntity(legalEntityId: string): void {
    const stored = sessionStorage.getItem(SessionStorageIds.AUTH_LEGAL_ENTITY_PERMISSIONS);
    const legalEntityPermissions = stored ? (JSON.parse(stored) as any[]) : [];
    const current = legalEntityPermissions.find(entry => entry.legalEntityId === legalEntityId);
    sessionStorage.setItem(SessionStorageIds.AUTH_PERMISSIONS, JSON.stringify(current?.permissions || []));
    sessionStorage.setItem(SessionStorageIds.AUTH_CURRENT_LEGAL_ENTITY_ID, legalEntityId);
    this.permissionService.refreshFromSession();
  }
}
